import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import curriculum, { Tier } from './curriculum';
import { INSTAGRAM_URL } from './config';
import './EducationSection.css';

/* ── Play icon SVG ── */
function PlayIcon() {
  return (
    <svg
      className="edu-play-icon"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 8.5L16 12L10 15.5V8.5Z" fill="currentColor" />
    </svg>
  );
}

/* ── Chevron icon SVG ── */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`edu-chevron ${open ? 'edu-chevron-open' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Tier badge label ── */
const tierBadge: Record<string, string> = {
  beginner: '01',
  intermediate: '02',
  advanced: '03',
};

/* ── Collapsible tier accordion ── */
function TierAccordion({ tier, isOpen, onToggle }: {
  tier: Tier;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`edu-tier ${isOpen ? 'edu-tier-open' : ''}`}>
      <button className="edu-tier-header" onClick={onToggle} aria-expanded={isOpen}>
        <span className="edu-tier-badge">{tierBadge[tier.id]}</span>
        <div className="edu-tier-info">
          <h3 className="edu-tier-label">{tier.label}</h3>
          <p className="edu-tier-tagline">{tier.tagline}</p>
        </div>
        <span className="edu-tier-count">{tier.lessons.length} lessons</span>
        <ChevronIcon open={isOpen} />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="edu-tier-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="edu-lessons">
              {tier.lessons.map((lesson, i) => (
                <motion.a
                  key={lesson.title}
                  href={lesson.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="edu-lesson-card"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                >
                  <span className="edu-lesson-number">{String(i + 1).padStart(2, '0')}</span>
                  <span className="edu-lesson-title">{lesson.title}</span>
                  <PlayIcon />
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main education section ── */
export default function EducationSection() {
  const [openTier, setOpenTier] = useState<string | null>('beginner');

  return (
    <section className="education-section" id="education">
      <div className="edu-header">
        <span className="edu-badge">100% FREE</span>
        <h2 className="section-title edu-glow-text">FREE TRADING EDUCATION</h2>
        <p className="section-sub edu-sub">
          A complete beginner-to-advanced curriculum. No paywalls, no upsells
          &mdash; just knowledge.
        </p>
      </div>

      <div className="edu-tiers">
        {curriculum.map((tier) => (
          <TierAccordion
            key={tier.id}
            tier={tier}
            isOpen={openTier === tier.id}
            onToggle={() => setOpenTier(openTier === tier.id ? null : tier.id)}
          />
        ))}
      </div>

      <div className="edu-cta-wrap">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="edu-cta"
        >
          Join &amp; Start Learning
        </a>
      </div>
    </section>
  );
}
